import { Observable, from } from "rxjs";
import { BehaviorSubject } from "rxjs";
import { Subject } from "rxjs";
import { config } from "../../../config/config";
import { CgpSnackbarService } from "../../shared/cgp-snackbar/cgp-snackbar.service";
import { cgpAuthentication } from "../../../modules/cgp-authentication";
import { CgpAlertDialogService } from "../../shared/cgp-alert-dialog/cgp-alert-dialog.service";
import { cgpCookieHelper } from "../../../modules/cgp-cookie-helper";
import { of } from "rxjs";
import { utilities } from "src/modules/utility";
import { mescServicesUrl } from "../../../config/config.base";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { tap } from "rxjs/operators";

const enum ResponseType {
  ARRAY_BUFFER = "arraybuffer",
  BLOB = "blob",
  JSON = "json",
  TEXT = "text",
}

const enum ResponseObserveType {
  RESPONSE = "response",
  BODY = "body",
}

export abstract class MescService extends BehaviorSubject<any[]> {
  public BASE_URL: string = mescServicesUrl[utilities.getEnv()];
  private http: HttpClient;
  private cgpSnackbarService: CgpSnackbarService;
  private cgpAlertDialogService: CgpAlertDialogService;

  protected static isImpersonate: boolean = false;
  hasChanged: boolean = false;
  filteredData: Subject<any[]> = new BehaviorSubject<any[]>([]);

  public data: any | any[] = [];
  public loading: Subject<boolean> = new BehaviorSubject<boolean>(false);
  public service = this;
  public toolTypeId: number = null;
  dataFieldName: string = "Items";

  public get Token(): string {
    return `Bearer ${cgpCookieHelper
      .retrieveCookie(config.authentication.authenticationCookie)
      .replace("Bearer_", "")}`;
  }

  constructor(private injector: Injector, public tableName: string) {
    super([]);
    this.http = injector.get(HttpClient);
    this.cgpSnackbarService = injector.get(CgpSnackbarService);
    this.cgpAlertDialogService = injector.get(CgpAlertDialogService);
    this.subscribe((x) => this.filteredData.next(x));
  }

  public query(
    urlExtension: string = "",
    isUseTableName: boolean = true
  ): void {
    let path = isUseTableName ? this.tableName : "";
    this.fetch(path, urlExtension)
      .toPromise()
      .then((x) => {
        this.updateToken(x.headers);
        const data = x.body;
        this.verifyData(data);
        this.loading.next(false);
        return this.data;
      })
      .catch((err) => this.handleError(err));
  }

  private handleResponse(response: Observable<any>): Promise<any> {
    return response.toPromise().then((x) => {
      try {
        this.updateToken(x.headers);
        const data = x.body;
        this.verifyData(data);
        return this.data;
      } catch (e) {
      } finally {
        this.loading.next(false);
      }
    });
  }

  public read(
    urlExtension = "",
    force = false,
    catchError = true
  ): Promise<any> {
    let path = this.BASE_URL + this.tableName + urlExtension;
    if (this.data.length !== 0 && !force && !this.hasChanged) {
      return of(this.data).toPromise();
    }

    const response = this.handleResponse(
      this.http.get(path, {
        headers: this.getHeaders(),
        observe: ResponseObserveType.RESPONSE,
        responseType: ResponseType.JSON,
      })
    );
    return catchError ? response.catch(this.handleError) : response;
  }

  public filterByValue(field: string, value: string) {
    super.next(this.data.filter((item) => item[field] === value));
  }

  public read2(
    urlExtension: string = "",
    force: boolean = false
  ): Promise<any> {
    let path = this.BASE_URL + this.tableName + urlExtension;

    return this.http
      .get(path, {
        headers: this.getHeaders(),
        observe: ResponseObserveType.RESPONSE,
        responseType: ResponseType.JSON,
      })
      .pipe(
        tap((x) => {
          this.updateToken(x.headers);
        })
      )
      .toPromise()
      .then((x) => {
        this.updateToken(x.headers);
        return x.body;
      });
  }

  public getById(id: string): void {
    let path = this.tableName + "/" + id;
    this.fetch(path)
      .toPromise()
      .then((data) => this.verifyData(data))
      .catch((err) => this.handleError(err));
  }

  public save(
    model: any,
    isNew: boolean,
    urlExtension: string = "",
    isDeleteId: boolean = true,
    catchError: boolean = true,
    includeId: boolean = true,
    responseType: any = null
  ): Promise<any> {
    this.hasChanged = true;
    let _user = parseInt(this.getCurrentUserGmin()),
      _date = this.getDate();
    if (isNew) {
      if (isDeleteId) delete model.Id;
      model["CreatedDate"] = _date;
      return this.create(model, urlExtension, catchError, responseType);
    } else {
      return this.update(model, urlExtension, catchError, includeId);
    }
  }

  public post(
    model: any,
    urlExtension: string,
    dataField: string = ""
  ): Promise<any> {
    this.hasChanged = true;
    return this.http
      .post(
        this.BASE_URL + this.tableName + urlExtension,
        JSON.stringify(model),
        {
          headers: this.getHeaders(),
          observe: ResponseObserveType.RESPONSE,
          responseType: ResponseType.JSON,
        }
      )
      .toPromise()
      .then((x) => {
        let data;
        try {
          this.updateToken(x.headers);
          data = x.body;
        } catch (e) {
        } finally {
          this.loading.next(false);
        }
        this.data = data[dataField] ? data[dataField] : data;
        return data;
      })
      .catch((err) => this.handleError(err));
  }

  private create(
    model: any,
    urlExtension: string,
    catchError: boolean,
    responseType: any
  ): Promise<any> {
    this.hasChanged = true;

    if (catchError) {
      return this.http
        .post(
          this.BASE_URL + this.tableName + urlExtension,
          JSON.stringify(model),
          {
            headers: this.getHeaders(),
            observe: ResponseObserveType.RESPONSE,
            responseType: ResponseType.JSON,
          }
        )
        .toPromise()
        .then((x) => {
          this.updateToken(x.headers);
          return x;
        })
        .catch(this.handleError);
    } else {
      return this.http
        .post(
          this.BASE_URL + this.tableName + urlExtension,
          JSON.stringify(model),
          {
            headers: this.getHeaders(),
            observe: ResponseObserveType.RESPONSE,
            responseType: ResponseType.JSON,
          }
        )
        .toPromise()
        .then((x) => {
          this.updateToken(x.headers);
          return x;
        });
    }
  }

  private update(
    model: any,
    urlExtension: string,
    catchError: boolean,
    includeId: boolean = true
  ): Promise<any> {
    this.hasChanged = true;
    let path = this.BASE_URL + this.tableName;
    if (this.tableName !== "camtl/Tool" && this.tableName !== "camtl/Contact")
      path += includeId ? "/" + model.Id : "";

    path += urlExtension;

    if (catchError) {
      return this.http
        .put(path, JSON.stringify(model), {
          headers: this.getHeaders(),
          observe: ResponseObserveType.RESPONSE,
          responseType: ResponseType.JSON,
        })
        .toPromise()
        .then((x) => {
          this.updateToken(x.headers);
          return x;
        })
        .catch(this.handleError);
    } else {
      return this.http
        .put(path, JSON.stringify(model), {
          headers: this.getHeaders(),
          observe: ResponseObserveType.RESPONSE,
          responseType: ResponseType.JSON,
        })
        .toPromise()
        .then((x) => {
          this.updateToken(x.headers);
          return x;
        });
    }
  }

  public remove(
    urlExtension: string = "",
    catchError: boolean = true
  ): Promise<any> {
    this.hasChanged = true;
    let url = this.BASE_URL + this.tableName + urlExtension;
    if (catchError) {
      return this.http
        .delete(url, {
          headers: this.getHeaders(),
          observe: ResponseObserveType.RESPONSE,
          responseType: ResponseType.JSON,
        })
        .toPromise()
        .then((x) => {
          this.updateToken(x.headers);
          return x;
        })
        .catch(this.handleError);
    } else {
      return this.http
        .delete(url, {
          headers: this.getHeaders(),
          observe: ResponseObserveType.RESPONSE,
          responseType: ResponseType.JSON,
        })
        .toPromise()
        .then((x) => {
          this.updateToken(x.headers);
          return x;
        });
    }
  }

  public upload(
    data: FormData,
    isNew: boolean = true,
    urlExtension: string = "",
    catchError: boolean = false
  ): Promise<any> {
    let token: string = cgpCookieHelper
      .retrieveCookie(config.authentication.authenticationCookie)
      .replace("Bearer_", "Bearer ");
    let headers = new Headers();
    headers.append("Accept", "application/json");
    headers.append("Authorization", token);

    if (catchError) {
      return this.http
        .post(this.BASE_URL + this.tableName + urlExtension, data, {
          headers: this.getHeaders(),
          observe: ResponseObserveType.RESPONSE,
          responseType: ResponseType.JSON,
        })
        .toPromise()
        .then((x) => {
          this.updateToken(x.headers);
          return x;
        })
        .catch(this.handleError);
    } else {
      return this.http
        .post(this.BASE_URL + this.tableName + urlExtension, data, {
          headers: this.getHeaders(),
          observe: ResponseObserveType.RESPONSE,
          responseType: ResponseType.JSON,
        })
        .toPromise()
        .then((x) => {
          this.updateToken(x.headers);
          return x;
        });
    }
  }

  public download(urlExtension: string = "", body: any = null): Promise<any> {
    let path = this.BASE_URL + this.tableName + urlExtension;
    if (body) {
      return this.http
        .post(path, body, {
          headers: this.getHeaders(),
          observe: ResponseObserveType.RESPONSE,
          responseType: ResponseType.BLOB,
        })
        .toPromise()
        .then((x) => {
          this.updateToken(x.headers);
          try {
            x.body;
          } catch (e) {}
          return x;
        });
    } else {
      return this.http
        .get(path, {
          headers: this.getHeaders(),
          observe: ResponseObserveType.RESPONSE,
          responseType: ResponseType.BLOB,
        })
        .toPromise()
        .then((x) => {
          this.updateToken(x.headers);
          try {
            x.body;
          } catch (e) {}
          return x;
        });
    }
  }

  public getUserByGmin(gmin: string): Promise<any> {
    return this.http
      .get(this.BASE_URL + "user/" + gmin, {
        headers: this.getHeaders(),
        observe: ResponseObserveType.RESPONSE,
        responseType: ResponseType.JSON,
      })
      .toPromise()
      .then((x) => {
        this.updateToken(x.headers);
        return x;
      });
  }

  private fetch(tableName: string, urlExtension: string = ""): Observable<any> {
    let path = this.BASE_URL + tableName + urlExtension;
    const response = this.http
      .get(path, {
        headers: this.getHeaders(),
        observe: ResponseObserveType.RESPONSE,
        responseType: ResponseType.JSON,
      })
      .toPromise()
      .then((x) => {
        this.updateToken(x.headers);
        return x;
      });
    return from(response);
  }

  private refreshToken = () => {
    cgpAuthentication.refreshToken(null);
    window.location.reload();
  };

  private handleError = (error: any) => {
    this.loading.next(false);
    if (error.status == "401") {
      if (this.tableName.toLowerCase().indexOf("user") == 0) {
        window.location.href = "#/unauthorized";
        return;
      }

      if (this.cgpAlertDialogService.isOpen()) return;

      this.cgpAlertDialogService.showAlertDialog({
        title: "HTTP Error - There was an error processing the request",
        // message: error + ". Click OK to redirect",
        message: error + ". Click OK to refresh token",
        alert: "danger",
        closeLabel: "OK",
        onClosed: () => this.refreshToken(),
      });
    } else if (error.status == "404") {
      if (this.tableName === "Program") {
        return;
      }
    } else if (
      error.message &&
      error.message.includes("Unexpected end of JSON input")
    ) {
      return;
    } else {
      this.cgpSnackbarService.showSnackbar({
        message:
          "There was an error processing the request for " +
          this.tableName +
          ": " +
          error.status +
          " " +
          error.statusText,
        type: "reject",
        duration: 3000,
      });
    }
  };

  private getDate(): string {
    let date = new Date();
    return date.toJSON();
  }

  private getCurrentUserGmin(): string {
    const userInfo = cgpAuthentication.getUserInfo();
    return userInfo.gmuniqueidentifier || userInfo.gmin;
  }

  public reset() {
    this.data = [];
    super.next(this.data);
  }

  public getToolsForSystemByToolType(systemId: string, toolTypeId: number) {
    return this.http
      .get(
        this.BASE_URL +
          this.tableName +
          "/" +
          systemId +
          "/toolType/" +
          toolTypeId,
        {
          headers: this.getHeaders(),
          observe: ResponseObserveType.RESPONSE,
          responseType: ResponseType.JSON,
        }
      )
      .map((response) => {
        this.loading.next(false);
        this.updateToken(response.headers);
        return response.body;
      })
      .toPromise()
      .then((data) => this.verifyData(data))
      .catch((err) => this.handleError(err));
  }

  public getBlockedToolNumbersForSystem(systemId: string): Promise<any> {
    return this.http
      .get(this.BASE_URL + "camtl/Tool/" + systemId + "/blockedtoolnumbers", {
        headers: this.getHeaders(),
        observe: ResponseObserveType.RESPONSE,
        responseType: ResponseType.JSON,
      })
      .map((response) => {
        this.loading.next(false);
        this.updateToken(response.headers);
        return response.body;
      })
      .toPromise()
      .then((data) => (this.data = data))
      .catch((err) => this.handleError(err));
  }

  public blockToolNumbers(
    systemId: string,
    blockQuantity: number
  ): Promise<any> {
    return this.http
      .post(
        this.BASE_URL +
          this.tableName +
          "/" +
          systemId +
          "/block?blockQuantity=" +
          blockQuantity,
        JSON.stringify(blockQuantity),
        { headers: this.getHeaders() }
      )
      .toPromise();
  }

  public getToolsByParentId(parentId: any): Promise<any> {
    return this.http
      .get(this.BASE_URL + "camtl/Tool/tools/" + parentId, {
        headers: this.getHeaders(),
        observe: ResponseObserveType.RESPONSE,
        responseType: ResponseType.JSON,
      })
      .map((response) => {
        this.loading.next(false);
        this.updateToken(response.headers);
        return response.body;
      })
      .toPromise()
      .then((data) => this.verifyData(data))
      .catch((err) => this.handleError(err));
  }

  public static setImpersonate(status: boolean = false) {
    MescService.isImpersonate = status;
  }
  public static getImpersonate() {
    return MescService.isImpersonate;
  }

  public resetFilter() {
    this.filteredData.next(this.value);
  }
  public handleFilter(value: string, field: string): void {
    let check: string = value.toString();
    let result = this.value.filter((x) => {
      try {
        let source: string = x[field].toString().toLowerCase();
        return source.indexOf(check.toLowerCase()) !== -1;
      } catch (e) {}
    });
    this.filteredData.next(result);
  }

  private getHeaders(): HttpHeaders {
    const headers = new HttpHeaders({ "Content-Type": "application/json" })
      .set("Accept", "application/json")
      .set("Authorization", this.Token);
    return headers;
  }

  private updateToken(headers: HttpHeaders) {
    try {
      let newToken = headers
        .get(config.authentication.authenticationCookie)
        .replace("Bearer ", "");
      cgpCookieHelper.setAuthCookie(newToken);
    } catch (e) {}
  }

  private verifyData(data: any): any[] {
    try {
      const hasDataList = Object.keys(data).some(
        (k) => k.toLowerCase() === "nextpagelink" || k.toLowerCase() === "count"
      );

      if (hasDataList) {
        Object.keys(data).forEach((k) => {
          if (Array.isArray(data[k])) {
            this.data = data[k];
            return true;
          }
          return false;
        });
      } else {
        this.data = data;
      }

      // By QZ0NCP - 1/23/2018
      // Warranty fix -- filter out all dataitems with null Id's
      if (this.data && this.data[0] && this.data[0].Id) {
        this.data = this.data.filter(
          (x) =>
            x.Id !== "00000000-0000-0000-0000-000000000000" ||
            x.Description !== "U"
        );
      }
    } catch (e) {
      return data;
    }

    super.next(this.data);
    this.hasChanged = false;
    return this.data;
  }
}

@Injectable()
export class BaseService extends MescService {
  constructor(injector: Injector) {
    super(injector, "");
  }
}

@Injectable()
export class CenterService extends MescService {
  constructor(injector: Injector) {
    super(injector, "Center");
  }
}

@Injectable()
export class RegionService extends MescService {
  constructor(injector: Injector) {
    super(injector, "Region");
  }
}

@Injectable()
export class GlobalRegionService extends MescService {
  constructor(injector: Injector) {
    super(injector, "GlobalRegion");
  }
}

@Injectable()
export class GroupService extends MescService {
  constructor(injector: Injector) {
    super(injector, "Group");
  }
}

@Injectable()
export class GroupUserService extends MescService {
  constructor(injector: Injector) {
    super(injector, "Group/Users");
  }
}

@Injectable()
export class ModelYearService extends MescService {
  constructor(injector: Injector) {
    super(injector, "ModelYear");
  }
}

@Injectable()
export class ModuleService extends MescService {
  constructor(injector: Injector) {
    super(injector, "Module");
  }
}

@Injectable()
export class RoleService extends MescService {
  constructor(injector: Injector) {
    super(injector, "Role");
  }
}

@Injectable()
export class PlantService extends MescService {
  constructor(injector: Injector) {
    super(injector, "Plant");
  }
}

@Injectable()
export class ProgramService extends MescService {
  constructor(injector: Injector) {
    super(injector, "Program");
  }
}

@Injectable()
export class ProgramDetailService extends MescService {
  constructor(injector: Injector) {
    super(injector, "Program/Detail");
  }
}

@Injectable()
export class ProgramDetailByIdService extends MescService {
  constructor(injector: Injector) {
    super(injector, "Program");
  }
}

@Injectable()
export class ToolHandednessService extends MescService {
  constructor(injector: Injector) {
    super(injector, "ToolHandedness");
  }
}

@Injectable()
export class UserService extends MescService {
  constructor(injector: Injector) {
    super(injector, "User");
  }
}

@Injectable()
export class UserProfileService extends MescService {
  constructor(injector: Injector) {
    super(injector, "User/Profile");
  }
}

@Injectable()
export class UserLastLoginDateService extends MescService {
  constructor(injector: Injector) {
    super(injector, "User/LastLoginDate");
  }
}

@Injectable()
export class ZoneService extends MescService {
  constructor(injector: Injector) {
    super(injector, "Zone");
  }
}

@Injectable()
export class SubZoneService extends MescService {
  constructor(injector: Injector) {
    super(injector, "SubZone");
  }
}

@Injectable()
export class SubZoneTitleService extends MescService {
  constructor(injector: Injector) {
    super(injector, "SubZoneTitle");
  }
}

@Injectable()
export class ProgramStyleService extends MescService {
  constructor(injector: Injector) {
    super(injector, "ProgramStyle");
  }
}

@Injectable()
export class TitleService extends MescService {
  constructor(injector: Injector) {
    super(injector, "Title");
  }
}

@Injectable()
export class PlantProgramService extends MescService {
  constructor(injector: Injector) {
    super(injector, "plantprogram");
  }
}
